import { useRef, forwardRef } from "react"
import styled from "@emotion/styled"
import { motion, useTransform, useMotionTemplate, useMotionValue, transform } from "framer-motion"

import { useConsole } from "contexts/Console"
import { useViewport } from "contexts/Viewport"
import getMediaQuery from "css/breakpoints"

import useSSR from "hooks/useSSR"

import Image from "components/media/ImageCLD"

import { useValue } from "./context"
import { Bg, ImgBlock, Block, SubContainer, Hgroup, Paragraph } from "./commons"

const ImgContainer = styled(motion.div)`
  grid-column: col 1 / span 6;
  transform: translate3d(0, 0, 0);
  width: 100%;

  ${getMediaQuery("m")} {
    grid-column: col 1 / span 6;
    align-self: center;
  }

  [dir="rtl"] & {
    ${getMediaQuery("m")} {
      grid-column: col 7 / span 6;
    }
  }

  & picture {
    display: flex;
  }

  & img {
    width: 100%;
    height: 100%;
  }
`

function Feature({ index, title, subtitle, text_part_1, text_part_2, image_portrait_cl, image_landscape_cl }, ref) {
  const console = useConsole()
  const ssr = useSSR()

  const bgRef = useRef()

  const { height, bp } = useViewport()

  const { globalProg, inputMap } = useValue()

  const insetVal = useMotionValue(0)
  const blu = useMotionValue(0)
  const opac = useMotionValue(1)

  const opacity = useTransform([globalProg, inputMap, height, bp], ([p, inp, h, b]) => {
    /**  guarantez same start up value as SSR */
    if (ssr) {
      blu.set(0)
      opac.set(1)
      insetVal.set(0)
      return 1
    } // TODO: what are the good starting values ?

    const [s, e] = b === "s" ? [0.35, 0.2] : [0.55, 0.35]
    const [sb, eb] = b === "s" ? [0, 0.2] : [0.55, 0.35]
    const arr = inp.split(",").map(str => Number(str))
    const input = [arr[index] - h * s, arr[index] - h * e, arr[index + 1] - h * s, arr[index + 1] - h * e]
    const inblu = [arr[index] + h * sb, arr[index] + h * eb]
    blu.set(b === "s" ? transform(p, inblu, [0, 10]) : 0)
    opac.set(b === "s" ? transform(p, inblu, [1, 0.5]) : 1)
    insetVal.set(arr[index] - p)
    return transform(p, input, [0, 1, 1, 0])
  })

  const inset = useMotionTemplate`inset(${insetVal}px 0 0 0)`
  const blur = useMotionTemplate`blur(${blu}px)`

  const [image_portrait, image_landscape] = [image_portrait_cl, image_landscape_cl].map(image => JSON.parse(image)[0])

  const image = {
    sources: [
      {
        public_id: image_portrait.src,
        width: "",
        height: "",
        // policy: image_portrait.policy,
        policy: "t_v7", // TODO: change back when correct one in JSON
        metadata: { v7_type: ["portrait"] },
      },
      {
        public_id: image_landscape.src,
        width: "",
        height: "",
        // policy: image_landscape.policy,
        policy: "t_v7", // TODO: change back when correct one in JSON
      },
    ],
  }

  return (
    <>
      <Bg ref={bgRef} style={{ zIndex: index }} />
      <ImgBlock style={{ clipPath: inset, zIndex: index }}>
        <ImgContainer style={{ filter: blur, opacity: opac }}>
          <Image {...image} sizes='(min-width: 48rem) 45vw, 84vw' />
        </ImgContainer>
      </ImgBlock>
      <Block ref={ref} style={{ zIndex: index }}>
        <SubContainer style={{ opacity }}>
          {title || subtitle ? (
            <Hgroup>
              {title ? <h2 dangerouslySetInnerHTML={{ __html: title }} /> : null}
              {subtitle ? <h3 dangerouslySetInnerHTML={{ __html: subtitle }} /> : null}
            </Hgroup>
          ) : null}
          {text_part_1 ? <Paragraph dangerouslySetInnerHTML={{ __html: text_part_1 }} /> : null}
          {text_part_2 ? <Paragraph dangerouslySetInnerHTML={{ __html: text_part_2 }} /> : null}
        </SubContainer>
      </Block>
    </>
  )
}

export default forwardRef(Feature)
